"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CLINICAL_DOCUMENT_REQUEST_EXAMPLES = exports.getDischargeSummaryForAdmissionRequestSchema = exports.getClinicalDocumentRequestSchema = exports.getClinicalDocumentVersionHistoryRequestSchema = exports.amendClinicalDocumentRequestSchema = exports.approveClinicalDocumentRequestSchema = exports.finalizeClinicalDocumentRequestSchema = exports.saveClinicalDocumentDraftRequestSchema = exports.createClinicalDocumentRequestSchema = exports.clinicalDocumentTypeSchema = void 0;
/**
 * Request contracts for the 6 Clinical Document endpoints. Field
 * shapes mirror `ClinicalDocumentWorkflowApplicationService`'s own
 * method signatures (frozen, Stage 6C.3) exactly — including
 * `approveDocument`'s caller-supplied `documentType`/`coSignerRoleIds`
 * (the Application Service accepts both explicitly rather than
 * resolving them itself; see that file's own header comment and
 * `CLINICAL-APPLICATION-REUSE.md` §6).
 */
const zod_1 = require("zod");
const clinical_common_request_1 = require("./clinical-common.request");
exports.clinicalDocumentTypeSchema = zod_1.z.enum([
    'SOAP',
    'PROGRESS_NOTE',
    'NURSING_NOTE',
    'PROCEDURE_NOTE',
    'ADMISSION_ASSESSMENT',
    'DISCHARGE_SUMMARY',
    'REFERRAL_LETTER',
]);
const documentContentChangesShape = {
    structuredContent: zod_1.z.record(zod_1.z.string(), zod_1.z.unknown()).optional(),
    freeTextContent: zod_1.z.string().optional(),
};
exports.createClinicalDocumentRequestSchema = zod_1.z.object({
    patientId: zod_1.z.string().uuid(),
    encounterId: zod_1.z.string().uuid(),
    documentType: exports.clinicalDocumentTypeSchema,
    templateId: zod_1.z.string().uuid().optional(),
    originatingOrderId: zod_1.z.string().uuid().optional(),
    authoredBy: zod_1.z.string().uuid(),
    /**
     * Discharge Summary, Phase 4A (ADR-01) — admission isolation. Only ever
     * meaningful for `documentType: 'DISCHARGE_SUMMARY'`; the other 6 types
     * have no admission concept and must omit it. Server-verified against
     * `patientId`/`encounterId` before anything is written — see
     * `ClinicalDocumentWorkflowApplicationService.assertAdmissionOwnership`.
     */
    ipAdmissionId: zod_1.z.string().uuid().optional(),
    ...documentContentChangesShape,
    ...clinical_common_request_1.clinicalWorkflowExtrasShape,
});
exports.saveClinicalDocumentDraftRequestSchema = zod_1.z.object({
    documentId: zod_1.z.string().uuid(),
    expectedLockVersion: zod_1.z.number().int().positive(),
    recordedBy: zod_1.z.string().uuid(),
    ...documentContentChangesShape,
    ...clinical_common_request_1.clinicalWorkflowExtrasShape,
});
exports.finalizeClinicalDocumentRequestSchema = zod_1.z.object({
    documentId: zod_1.z.string().uuid(),
    signedBy: zod_1.z.string().uuid(),
    expectedLockVersion: zod_1.z.number().int().positive(),
    ...clinical_common_request_1.clinicalWorkflowExtrasShape,
});
exports.approveClinicalDocumentRequestSchema = zod_1.z.object({
    documentId: zod_1.z.string().uuid(),
    documentType: exports.clinicalDocumentTypeSchema,
    coSignedBy: zod_1.z.string().uuid(),
    coSignerRoleIds: zod_1.z.array(zod_1.z.string().uuid()),
    expectedLockVersion: zod_1.z.number().int().positive(),
    ...clinical_common_request_1.clinicalWorkflowExtrasShape,
});
exports.amendClinicalDocumentRequestSchema = zod_1.z.object({
    originalDocumentId: zod_1.z.string().uuid(),
    authoredBy: zod_1.z.string().uuid(),
    ...documentContentChangesShape,
    ...clinical_common_request_1.clinicalWorkflowExtrasShape,
});
/** Read-only — `getVersionHistory(documentId)` takes no pagination or filter parameter (`CLINICAL-CONTRACT-REUSE.md` §3). */
exports.getClinicalDocumentVersionHistoryRequestSchema = zod_1.z.object({
    documentId: zod_1.z.string().uuid(),
});
/** Discharge Summary, Phase 4B — `getDocument(documentId)` takes no body, params-only, mirroring `getClinicalDocumentVersionHistoryRequestSchema` exactly. */
exports.getClinicalDocumentRequestSchema = zod_1.z.object({
    documentId: zod_1.z.string().uuid(),
});
/** Discharge Summary, Phase 4C — IP Billing's "does this admission have a discharge summary" question. Params-only. */
exports.getDischargeSummaryForAdmissionRequestSchema = zod_1.z.object({
    ipAdmissionId: zod_1.z.string().uuid(),
});
exports.CLINICAL_DOCUMENT_REQUEST_EXAMPLES = {
    createClinicalDocument: {
        patientId: '11111111-1111-1111-1111-111111111111',
        encounterId: '55555555-5555-5555-5555-555555555555',
        documentType: 'SOAP',
        freeTextContent: 'Patient reports mild headache, no fever.',
        authoredBy: '77777777-7777-7777-7777-777777777777',
    },
    saveClinicalDocumentDraft: {
        documentId: '88888888-8888-8888-8888-888888888888',
        expectedLockVersion: 1,
        recordedBy: '77777777-7777-7777-7777-777777777777',
        freeTextContent: 'Patient reports mild headache, no fever. BP 120/80.',
    },
    finalizeClinicalDocument: {
        documentId: '88888888-8888-8888-8888-888888888888',
        signedBy: '77777777-7777-7777-7777-777777777777',
        expectedLockVersion: 2,
    },
    approveClinicalDocument: {
        documentId: '88888888-8888-8888-8888-888888888888',
        documentType: 'ADMISSION_ASSESSMENT',
        coSignedBy: '99999999-9999-9999-9999-999999999999',
        coSignerRoleIds: ['aaaaaaaa-aaaa-aaaa-aaaa-aaaaaaaaaaaa'],
        expectedLockVersion: 1,
    },
    amendClinicalDocument: {
        originalDocumentId: '88888888-8888-8888-8888-888888888888',
        authoredBy: '77777777-7777-7777-7777-777777777777',
        freeTextContent: 'Correction: BP was 122/82, not 120/80.',
    },
    getClinicalDocumentVersionHistory: {
        documentId: '88888888-8888-8888-8888-888888888888',
    },
    getClinicalDocument: {
        documentId: '88888888-8888-8888-8888-888888888888',
    },
    getDischargeSummaryForAdmission: {
        ipAdmissionId: '99999999-9999-9999-9999-999999999999',
    },
};
//# sourceMappingURL=clinical-document.request.js.map